import React from "react";
import { Box, InputAdornment, TextField, Typography } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";

import { useServices, useSnackbar } from "contexts";
import { getUserRank, getTodoTemplates } from "services";
import {
  Session,
  getAverageScore,
  getPercentageChange,
  getNumberWithSuffix,
  getTwoDigitNo,
  round,
} from "utils";

import { useNavigateWithClient } from "hooks";

import MobileAppShell from "../../layout/MobileAppShell";
import RecentAttemptsAnalytics from "../../components/RecentAttemptsAnalytics";
import InterviewInstructionsModal from "../../components/InterviewInstructionsModal";
import InterviewCountdown from "../../components/InterviewCountdown";
import BatchSelector from "../../components/BatchSelector";
import CardSlider from "../../components/CardSlider";
import NullState from "../../components/NullState";
import { useSelectedBatch } from "../../hooks";
import StatCards from "./StatCards";
import AssessmentCard, { AssessmentCardSkeleton } from "./AssessmentCard";
import Chips from "./Chips";
import { mapTodoTemplates } from "./mapTemplates";

// Assessment cards are paged rather than listed — swipe for the next set.
const CARDS_PER_PAGE = 5;

const FILTERS = [
  { field: "expiresSoon", label: "Expires Soon" },
  { field: "new", label: "New Published" },
  { field: "assignment", label: "Assignment" },
  { field: "practice", label: "Practice" },
];

function buildStats(batchData, rank) {
  const rankValue = rank?.rank > 0 ? getNumberWithSuffix(rank.rank) : "—";

  const userAvg = batchData
    ? round(getAverageScore(batchData.user.communication, batchData.user.content))
    : "—";

  let batchAvg = "—";
  let batchDelta = null;
  if (batchData) {
    const curr = getAverageScore(batchData.communication, batchData.content);
    const prev = getAverageScore(
      batchData.prevCommunication,
      batchData.prevContent
    );
    batchAvg = round(curr);

    const diff = getPercentageChange(curr, prev);
    if (Number.isFinite(diff) && Math.round(diff) !== 0) {
      batchDelta = { value: `${Math.abs(round(diff))}%`, up: diff >= 0 };
    }
  }

  // `info` / `deltaInfo` are desktop's two tooltips per tile, verbatim from
  // `components/Home/index.jsx` (subTitle / info). Desktop's rank card carries no
  // tooltip, so its copy comes from the dashboard tour's "My Standing" step.
  return [
    {
      key: "rank",
      label: "Your Rank",
      value: rankValue,
      info: "Where you stand amongst your peers in this batch",
    },
    {
      key: "average",
      label: "Your Average",
      value: userAvg,
      info: "Your Cumulative Assessment Average Score",
      deltaInfo:
        "Reflects changes in your overall average score after the last attempt",
    },
    {
      key: "batchAverage",
      label: "Batch Average",
      value: batchAvg,
      delta: batchDelta,
      info: "Average score representing your batch's performance",
      deltaInfo:
        "Reflects changes in the overall average score of the batch after the last assessment",
    },
  ];
}

export default function MobileDashboard() {
  const service = useServices();
  const snackbar = useSnackbar();

  const {
    batches,
    batchData,
    loading: batchLoading,
    selectBatch,
  } = useSelectedBatch();

  const firstName = (Session?.getUser?.()?.name || "").split(" ")[0] || "there";

  const [rank, setRank] = React.useState(null);
  const [templates, setTemplates] = React.useState([]);
  const [templatesLoading, setTemplatesLoading] = React.useState(false);
  const navigateWithClient = useNavigateWithClient();

  const [search, setSearch] = React.useState("");
  const [activeFilter, setActiveFilter] = React.useState(null);
  const [selectedTemplate, setSelectedTemplate] = React.useState(null);
  const [countdownTemplate, setCountdownTemplate] = React.useState(null);

  const batchId = batchData?._id;

  // "Let's Go" — close the instructions modal and run the countdown.
  const handleStartInterview = () => {
    setCountdownTemplate(selectedTemplate);
    setSelectedTemplate(null);
  };

  // After the countdown, proceed to the interview screen — same target as the
  // desktop `proceedToTest` (navigateWithClient('/assessment?tid=<id>')).
  // The mobile /assessment screen isn't built yet, so this currently lands on
  // the NotAvailable fallback.
  const handleCountdownComplete = () => {
    const tid = countdownTemplate?._id;
    setCountdownTemplate(null);
    if (tid) navigateWithClient(`/assessment?tid=${tid}`);
  };

  // Rank (also drives the tagline number on desktop).
  React.useEffect(() => {
    setRank(null); // rank is per-batch — don't show the old batch's while loading
    if (!batchId || batchData?.forPathway) return;
    (async () => {
      try {
        const userRank = await service.callService(getUserRank, { batch: batchId });
        setRank(userRank);
      } catch (error) {
        console.error(error);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchId]);

  // "My Assessments" todo templates.
  React.useEffect(() => {
    if (!batchId) return;
    (async () => {
      try {
        setTemplatesLoading(true);
        const todo = await service.callService(getTodoTemplates, batchId);
        setTemplates(mapTodoTemplates(todo || []));
      } catch (error) {
        console.error(error);
        snackbar.error("Unable to load assessments");
      } finally {
        setTemplatesLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchId]);

  const stats = React.useMemo(() => buildStats(batchData, rank), [batchData, rank]);

  // The templates fetch can't start until the batch resolves, so cover the batch
  // load too — otherwise the list flashes "No assessments found." first.
  const showTemplateSkeletons = batchLoading || templatesLoading;

  const filteredTemplates = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    return templates.filter((t) => {
      const matchesSearch = !q || (t.title || "").toLowerCase().includes(q);
      const matchesFilter =
        !activeFilter ||
        (activeFilter === "practice" && t.type === "practice") ||
        (activeFilter === "assignment" && t.type === "assignment") ||
        (activeFilter === "expiresSoon" && t.expiresSoon) ||
        (activeFilter === "new" && t.isNew);
      return matchesSearch && matchesFilter;
    });
  }, [templates, search, activeFilter]);

  return (
    <MobileAppShell title="Dashboard">
      <Box sx={{ px: 4, py: 4, display: "flex", flexDirection: "column", gap: 5 }}>
        {/* Welcome header */}
        <Box>
          <Typography variant="h5-bold" color="primary.clr-300">
            Welcome {firstName},
          </Typography>
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, mt: 1 }}>
            <Typography variant="body02-regular" color="neutral.clr-600">
              Thriving among the Elite! Your dedication and progress are
              commendable. Let&apos;s continue pushing boundaries!
            </Typography>
            <Box component="span" sx={{ fontSize: 20, lineHeight: 1 }}>
              🏆
            </Box>
          </Box>
        </Box>

        {/* Batch picker — drives every section below it */}
        <BatchSelector
          batches={batches}
          value={batchData}
          onChange={selectBatch}
          loading={batchLoading}
        />

        {/* Stat tiles */}
        <StatCards stats={stats} loading={batchLoading} />

        {/* My Assessments */}
        <Box
          sx={{
            bgcolor: "shades.clr-white-900",
            border: "1px solid",
            borderColor: "neutral.clr-100",
            borderRadius: 3,
            p: 4,
            display: "flex",
            flexDirection: "column",
            gap: 4,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 3,
            }}
          >
            <Box>
              <Typography
                variant="h6-semiBold"
                color="neutral.clr-800"
                sx={{ display: "block" }}
              >
                My Assessments
              </Typography>
              <Typography
                variant="body02-regular"
                color="neutral.clr-500"
                sx={{ display: "block", mt: 0.5 }}
              >
                Total :&nbsp;
                <Typography component="span" variant="body02-bold" color="primary.clr-300">
                  {getTwoDigitNo(filteredTemplates.length)}
                </Typography>
              </Typography>
            </Box>
            <TextField
              size="small"
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{
                maxWidth: 132,
                "& .MuiOutlinedInput-root": {
                  height: 32,
                  borderRadius: 2,
                  pl: 2,
                },
                "& .MuiOutlinedInput-input": {
                  py: 0,
                  pl: 1,
                  fontSize: 12,
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" sx={{ mr: 0 }}>
                    <SearchIcon sx={{ fontSize: 16, color: "neutral.clr-400" }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {/* Nothing to filter until the batch has published something — the
              design drops the whole row in that null state. */}
          {!!templates.length && (
            <Box>
              <Typography
                variant="body02-regular"
                color="neutral.clr-500"
                sx={{ mb: 2, display: "block" }}
              >
                Topic-based filters
              </Typography>
              <Chips items={FILTERS} value={activeFilter} onChange={setActiveFilter} />
            </Box>
          )}

          {showTemplateSkeletons ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              {Array.from(new Array(CARDS_PER_PAGE)).map((_, i) => (
                <AssessmentCardSkeleton key={i} />
              ))}
            </Box>
          ) : filteredTemplates.length ? (
            <CardSlider
              items={filteredTemplates}
              pageSize={CARDS_PER_PAGE}
              renderItem={(assessment) => (
                <AssessmentCard
                  key={assessment.id}
                  assessment={assessment}
                  onClick={() => setSelectedTemplate(assessment.raw)}
                />
              )}
            />
          ) : templates.length ? (
            // Templates exist — the search/filter just matched nothing.
            <NullState
              icon={AssignmentOutlinedIcon}
              description={
                activeFilter
                  ? "No assessment found for the selected filter!"
                  : "No assessment found"
              }
              onClearFilters={
                activeFilter ? () => setActiveFilter(null) : undefined
              }
            />
          ) : (
            <NullState
              icon={AssignmentOutlinedIcon}
              description={
                <>
                  No assessment is published for your batch so far!
                  <br />
                  once published you can see in this section
                </>
              }
            />
          )}
        </Box>

        {/* Recent Attempts Analytics — section below My Assessments */}
        <RecentAttemptsAnalytics batch={batchId} batchLoading={batchLoading} />
      </Box>

      <InterviewInstructionsModal
        open={!!selectedTemplate}
        template={selectedTemplate}
        onClose={() => setSelectedTemplate(null)}
        onStart={handleStartInterview}
      />

      <InterviewCountdown
        open={!!countdownTemplate}
        onComplete={handleCountdownComplete}
      />
    </MobileAppShell>
  );
}
